import React, { useContext, useState, createContext, useEffect, ReactNode } from 'react';
import { Tag, User } from '@sm-dashboard/types';
import instanceAxios from '@/util/axios';
import { getShluchimByIdUrl, getTagsUrl, isNewLeadsCountUrl, shluchimAllUrl, branchsAllUrl } from '@/constants';
import { AxiosError } from 'axios';
import { UserPermission } from '@/util/Permission';

export type AutocompleteType = {
  label: string;
  value: string;
};

type Branch = {
  label: string;
  value: number;
};

type DataContextType = {
  user: User | undefined;
  setUser: React.Dispatch<React.SetStateAction<User | undefined>>;
  isNewLeadsCount: number;
  setIsNewLeadsCount: React.Dispatch<React.SetStateAction<number>>;
  allShluchim: AutocompleteType[];
  setAllShluchim: React.Dispatch<React.SetStateAction<AutocompleteType[]>>;
  shaliach: User | undefined;
  setShaliach: React.Dispatch<React.SetStateAction<User | undefined>>;
  userPermission: UserPermission;
  tags: Tag[];
  branches: Branch[];
  setBranches: React.Dispatch<React.SetStateAction<Branch[]>>;
};

const DataContext = createContext<DataContextType>(null!);
export const useDataContext = () => useContext(DataContext);

export const DataContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User>();
  const [isNewLeadsCount, setIsNewLeadsCount] = useState<number>(0);
  const [allShluchim, setAllShluchim] = useState<AutocompleteType[]>([]);
  const [shaliach, setShaliach] = useState<User>();
  const [userPermission, setUserPermission] = useState<UserPermission>(null!);
  const [tags, setTags] = useState<Tag[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const { data: user } = await instanceAxios.get(getShluchimByIdUrl);
        const { data: tags } = await instanceAxios.get(getTagsUrl);
        if (user?.reLogin) {
          window.localStorage.removeItem('token');
          setUser(undefined);
        }
        setUserPermission(
          new UserPermission(
            tags?.data || [],
            user?.tags || [],
            user?.crmMembers || [],
            user?.bidirectionalWorkMembers || [],
            user?.workMembers || [],
            user?.paymentMembers || []
          )
        );

        const { data: leadsData } = await instanceAxios.get(isNewLeadsCountUrl);
        const { data: shluchimData } = await instanceAxios.get(shluchimAllUrl);
        setAllShluchim(shluchimData);
        setIsNewLeadsCount(leadsData);
        setShaliach(user);
        setTags(tags.data);

        const { data: branchesResponse } = await instanceAxios.get(`${branchsAllUrl}?gallery=true`);
        setBranches(branchesResponse);
      } catch (error: AxiosError | any) {
        console.log(error);
      }
    };
    fetchInitialData();
  }, [user]);

  return (
    <DataContext.Provider
      value={{
        user,
        setUser,
        isNewLeadsCount,
        setIsNewLeadsCount,
        allShluchim,
        setAllShluchim,
        shaliach,
        setShaliach,
        userPermission,
        tags,
        branches,
        setBranches
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
